import prettyMilliseconds from "pretty-ms";
import React, { ReactElement, useState, useEffect } from "react";
import { BsCheckCircle } from "react-icons/bs";
import { RiErrorWarningFill } from "react-icons/ri";
import TimeAgo from "react-timeago";
import {
    DueDateType,
    TicketDueDate,
} from "../../__generated__/api";

interface TicketTasksProps {
    dueDates: TicketDueDate[];
    isDueDateHistoryExpanded: boolean;
}
const Titles = {
    [DueDateType.SlaFirstResponse]: "First response",
    [DueDateType.SlaTimeToUpdate]: "Next update",
    [DueDateType.SlaRequesterWaitTime]: "Requester Wait time",

};

function TicketDueDatesRow({ ticketDueDate, isDueDateHistoryExpanded }: {
    ticketDueDate: TicketDueDate;
    isDueDateHistoryExpanded: boolean;
}): ReactElement | null {
    const dueDate = new Date(ticketDueDate.dueDate);

    const completedDate = ticketDueDate.completedDate ? new Date(ticketDueDate.completedDate) : undefined;
    const [isInViolation, setIsInViolation] = useState(dueDate.getTime() < (completedDate?.getTime() ?? Date.now()));

    const shouldDisplay = isDueDateHistoryExpanded || !ticketDueDate.completedDate;
    useEffect(() => {
        if (!completedDate) {
            const timeLeft = dueDate.getTime() - Date.now();
            if (timeLeft > 0) {
                const timeout = setTimeout(() => {
                    setIsInViolation(!ticketDueDate.completedDate && dueDate.getTime() < Date.now());
                }, timeLeft);

                return () => {
                    clearTimeout(timeout);
                };
            }
        }
        return undefined;
    }, [!completedDate && shouldDisplay]);

    if (!shouldDisplay) {
        return null;
    }

    let humanizedDate;
    if (completedDate) {
        const start = new Date(ticketDueDate.startedDate ?? Date.now());
        const slaTime = prettyMilliseconds(dueDate.getTime() - start.getTime(), { verbose: true });
        const duration = prettyMilliseconds(completedDate.getTime() - start.getTime(), {
            secondsDecimalDigits: 0,
        });
        humanizedDate = <span title={`Took ${duration}, the SLA was: ${slaTime}`}>{duration}</span>;
    }

    return (
        <div className="list-group-item px-0 ">

            <div className="d-flex">
                <small className={`d-flex justify-content-between w-100 ${isInViolation ? "text-danger fw-strong" : ""}`}>
                    <span>
                        {isInViolation && (<RiErrorWarningFill className="me-2" title="SLA in violation" />)}
                        {!isInViolation && completedDate && <BsCheckCircle className="text-success me-2" title="Done" /> }
                        {Titles[ticketDueDate.type]}
                    </span>
                    <span>

                        {humanizedDate ?? (
                            <TimeAgo date={dueDate} title={dueDate.toLocaleString()} />
                        )}

                    </span>

                </small>
            </div>

        </div>
    );
}

export default function TicketDueDates({
    dueDates,
    isDueDateHistoryExpanded,
}: TicketTasksProps): ReactElement {
    return (
        <div className="list-group list-group-flush border-bottom">
            {dueDates.map(dueDate => (
                <TicketDueDatesRow
                    ticketDueDate={dueDate}
                    key={dueDate.type + dueDate.dueDate + dueDate.completedDate}
                    isDueDateHistoryExpanded={isDueDateHistoryExpanded}
                />
            ))}
        </div>
    );
}
